import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Calculator, Info, ChevronDown } from "lucide-react";
import { CalculatorInputs, CalculationResults, FormulaType } from "@/types/calculator";
import { calculateResults } from "@/lib/calculations";
import { toast } from "sonner";
import PresetSelector from "./PresetSelector";
import { ACTIVITY_PRESETS } from "@/lib/presets";
import { Checkbox } from "@/components/ui/checkbox";
import WearableImportDialog from "@/components/WearableImportDialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface CalculatorFormProps {
  onCalculate: (results: CalculationResults, inputs: CalculatorInputs) => void;
}

const CalculatorForm = ({ onCalculate }: CalculatorFormProps) => {
  const [unitSystem, setUnitSystem] = useState<"metric" | "imperial">("metric");
  const [selectedFormulas, setSelectedFormulas] = useState<FormulaType[]>(["ACSM", "MET", "Mifflin"]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [inputs, setInputs] = useState<Partial<CalculatorInputs>>({
    age: 30,
    sex: "male",
    height: unitSystem === "metric" ? 170 : 67,
    weight: unitSystem === "metric" ? 75 : 165,
    speed: unitSystem === "metric" ? 5 : 3,
    duration: 30,
    frequency: 5,
    terrain: "flat",
    intensity: "moderate",
    activityLevel: "light",
    goal: "0.5",
  });

  const handleUnitToggle = (checked: boolean) => {
    const newSystem = checked ? "imperial" : "metric";
    setUnitSystem(newSystem);
    
    setInputs(prev => ({
      ...prev,
      height: newSystem === "imperial" 
        ? Math.round((prev.height || 0) / 2.54) 
        : Math.round((prev.height || 0) * 2.54),
      weight: newSystem === "imperial"
        ? Math.round((prev.weight || 0) * 2.20462)
        : Math.round((prev.weight || 0) / 2.20462),
      speed: newSystem === "imperial"
        ? Number(((prev.speed || 0) / 1.60934).toFixed(1))
        : Number(((prev.speed || 0) * 1.60934).toFixed(1)),
    }));
  };

  const handlePresetSelect = (preset: typeof ACTIVITY_PRESETS[0]) => {
    setInputs(prev => ({
      ...prev,
      speed: unitSystem === "metric" ? preset.speed.metric : preset.speed.imperial,
      intensity: preset.intensity,
      terrain: preset.terrain,
    }));
    toast.success(`${preset.name} preset applied!`);
  };

  const toggleFormula = (formula: FormulaType) => {
    setSelectedFormulas(prev => {
      if (prev.includes(formula)) {
        return prev.filter(f => f !== formula);
      }
      return [...prev, formula];
    });
  };

  const handleCalculate = () => {
    if (selectedFormulas.length === 0) {
      toast.error("Please select at least one formula");
      return;
    }

    try {
      const fullInputs = { ...inputs, unitSystem, selectedFormulas } as CalculatorInputs;
      const results = calculateResults(fullInputs);
      onCalculate(results, fullInputs);
      toast.success("Calculation complete!", {
        description: `Estimated ${Math.round(results.caloriesPerSession)} calories per session`,
      });
    } catch (error) {
      toast.error("Calculation error", {
        description: "Please check your inputs and try again.",
      });
    }
  };

  const updateInput = (field: keyof CalculatorInputs, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Card className="p-3 md:p-6 border-card-border rounded-2xl shadow-soft">
      <div className="space-y-3 md:space-y-6">
        <div className="flex items-center justify-between pb-2 md:pb-4 border-b border-card-border">
          <h2 className="text-base md:text-xl font-semibold text-foreground">Your Details</h2>
          <div className="flex items-center gap-1 md:gap-2">
            <Label htmlFor="unit-toggle" className="text-xs md:text-sm font-medium">
              {unitSystem === "metric" ? "Metric" : "Imperial"}
            </Label>
            <Switch
              id="unit-toggle"
              checked={unitSystem === "imperial"}
              onCheckedChange={handleUnitToggle}
            />
          </div>
        </div>

        {/* Presets */}
        <div className="w-full">
          <PresetSelector unitSystem={unitSystem} onSelectPreset={handlePresetSelect} />
        </div>

        {/* Formula Selection */}
        <div className="space-y-3">
          <Label className="text-sm font-semibold">Calculation Methods</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              { id: "ACSM" as FormulaType, name: "ACSM Walking", desc: "Gold standard" },
              { id: "MET" as FormulaType, name: "MET Compendium", desc: "Research-based" },
              { id: "Mifflin" as FormulaType, name: "Mifflin-St Jeor", desc: "BMR method" },
              { id: "Katch" as FormulaType, name: "Katch-McArdle", desc: "Requires body fat%" },
            ].map((formula) => {
              const isDisabled = formula.id === "Katch" && !inputs.bodyFat;
              return (
                <div 
                  key={formula.id} 
                  className={`flex items-start space-x-2 p-2 rounded-lg ${
                    isDisabled ? 'bg-muted/30 opacity-60' : 'bg-secondary/50'
                  }`}
                >
                  <Checkbox
                    id={formula.id}
                    checked={selectedFormulas.includes(formula.id)}
                    onCheckedChange={() => toggleFormula(formula.id)}
                    disabled={isDisabled}
                    className={isDisabled ? "cursor-not-allowed" : ""}
                  />
                  <div className="flex-1">
                    <Label 
                      htmlFor={formula.id}
                      className={`text-xs font-medium ${
                        isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                      }`}
                    >
                      {formula.name}
                      {isDisabled && (
                        <span className="ml-1 text-xs text-destructive">
                          (Add body fat % in Advanced Options)
                        </span>
                      )}
                    </Label>
                    <p className="text-xs text-muted-foreground">{formula.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <div className="space-y-2">
            <Label htmlFor="age" className="text-sm">Age</Label>
            <Input
              id="age"
              type="number"
              min={10}
              max={120}
              value={inputs.age || ""}
              onChange={(e) => updateInput("age", Number(e.target.value))}
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sex" className="text-sm">Sex</Label>
            <Select value={inputs.sex} onValueChange={(v) => updateInput("sex", v)}>
              <SelectTrigger id="sex" className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border z-50">
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Height & Weight */}
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <div className="space-y-2">
            <Label htmlFor="height" className="text-sm">
              Height ({unitSystem === "metric" ? "cm" : "in"})
            </Label>
            <Input
              id="height"
              type="number"
              value={inputs.height || ""}
              onChange={(e) => updateInput("height", Number(e.target.value))}
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="weight" className="text-sm">
              Weight ({unitSystem === "metric" ? "kg" : "lb"})
            </Label>
            <Input
              id="weight"
              type="number"
              value={inputs.weight || ""}
              onChange={(e) => updateInput("weight", Number(e.target.value))}
              className="h-10"
            />
          </div>
        </div>

        {/* Advanced Options - Collapsible */}
        <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen} className="space-y-3">
          <CollapsibleTrigger asChild>
            <Button variant="outline" className="w-full justify-between h-10" type="button">
              <span className="text-sm font-medium">Advanced Options</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${advancedOpen ? "rotate-180" : ""}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 pt-2">
            <div className="space-y-2">
              <Label htmlFor="bodyFat" className="flex items-center gap-2 text-sm">
                Body Fat % <span className="text-xs text-muted-foreground">(for Katch-McArdle)</span>
              </Label>
              <Input
                id="bodyFat"
                type="number"
                min={5}
                max={50}
                placeholder="Optional"
                value={inputs.bodyFat || ""}
                onChange={(e) => updateInput("bodyFat", e.target.value ? Number(e.target.value) : undefined)}
                className="h-10"
              />
            </div>
            <div>
              <Label className="text-sm mb-2 block">Import Wearable Data</Label>
              <WearableImportDialog onImport={(data) => {
                toast.success("Wearable data imported! Adjust your inputs as needed.");
              }} />
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Walking Details */}
        <div className="pt-3 md:pt-4 border-t border-card-border">
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-3 md:mb-4">Walking Details</h3>
          
          <div className="grid grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">
            <div className="space-y-2">
              <Label htmlFor="speed" className="text-sm">
                Speed ({unitSystem === "metric" ? "km/h" : "mph"})
              </Label>
              <Input
                id="speed"
                type="number"
                step="0.1"
                value={inputs.speed || ""}
                onChange={(e) => updateInput("speed", Number(e.target.value))}
                className="h-10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration" className="text-sm">Duration (min)</Label>
              <Input
                id="duration"
                type="number"
                value={inputs.duration || ""}
                onChange={(e) => updateInput("duration", Number(e.target.value))}
                className="h-10"
              />
            </div>
          </div>

          <div className="space-y-2 mb-3 md:mb-4">
            <Label htmlFor="frequency" className="text-sm">Sessions per week</Label>
            <Input
              id="frequency"
              type="number"
              min={0}
              max={21}
              value={inputs.frequency || ""}
              onChange={(e) => updateInput("frequency", Number(e.target.value))}
              className="h-10"
            />
          </div>

          <div className="grid grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">
            <div className="space-y-2">
              <Label htmlFor="terrain" className="text-sm">Terrain</Label>
              <Select value={inputs.terrain} onValueChange={(v) => updateInput("terrain", v)}>
                <SelectTrigger id="terrain" className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border z-50">
                  <SelectItem value="flat">Flat</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="hilly">Hilly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="intensity" className="text-sm">Intensity</Label>
              <Select value={inputs.intensity} onValueChange={(v) => updateInput("intensity", v)}>
                <SelectTrigger id="intensity" className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border z-50">
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="brisk">Brisk</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2 mb-3 md:mb-4">
            <Label htmlFor="activityLevel" className="text-sm">Baseline Activity Level</Label>
            <Select value={inputs.activityLevel} onValueChange={(v) => updateInput("activityLevel", v)}>
              <SelectTrigger id="activityLevel" className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border z-50">
                <SelectItem value="sedentary">Sedentary</SelectItem>
                <SelectItem value="light">Lightly Active</SelectItem>
                <SelectItem value="moderate">Moderately Active</SelectItem>
                <SelectItem value="very active">Very Active</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="goal" className="flex items-center gap-2 text-sm">
              Goal (kg/week loss or "maintain")
              <Info className="w-3 h-3 text-muted-foreground" />
            </Label>
            <Input
              id="goal"
              type="text"
              placeholder="e.g., 0.5 or maintain"
              value={inputs.goal || ""}
              onChange={(e) => updateInput("goal", e.target.value)}
              className="h-10"
            />
          </div>
        </div>

        {/* Calculate Button - Sticky on Mobile */}
        <div className="sticky bottom-0 left-0 right-0 bg-card/95 backdrop-blur-sm p-4 -mx-4 -mb-4 md:mb-0 md:static md:p-0 md:bg-transparent md:backdrop-blur-none border-t md:border-t-0 border-border/50 z-10">
          <Button 
            onClick={handleCalculate}
            className="w-full bg-gradient-primary hover:opacity-90 transition-opacity h-12 md:h-12 text-base font-semibold shadow-lg md:shadow-none"
            size="lg"
          >
            <Calculator className="w-5 h-5 mr-2" />
            Calculate Results
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default CalculatorForm;
